import { z } from "zod";

import { CompanyFindPersonStatus, CompanyShowBucket } from "@signalist/shared-types";

import { AssignStatus } from "@/enum/list.enum";
import { api } from "@/redux/api";

/**
 * The Companies tab (PREC-4400): a company-only list's company-placeholder rows, read from
 * `GET list_person/companies`. Separate from list.api.ts, which is at its size cap. The rows are
 * not leads — the lead tabs never show them — so this has its own endpoint and schema.
 *
 * Every count is a SQL aggregate the API serialises as a string, so each one is `z.coerce.number`
 * (web CLAUDE.md §6).
 */

export const CompanyPersonFoundSchema = z.object({
	id: z.number(),
	prospectId: z.number(),
	name: z.string().nullish(),
	firstName: z.string().nullish(),
	lastName: z.string().nullish(),
	linkedinUrl: z.string().nullish(),
	title: z.string().nullish(),
	assignStatus: z.nativeEnum(AssignStatus).nullish(),
});

export const CompanyRowOrganizationSchema = z.object({
	id: z.number(),
	name: z.string().nullish(),
	logoUrl: z.string().nullish(),
	industry: z.string().nullish(),
	country: z.string().nullish(),
	employeesFrom: z.coerce.number().nullish(),
	employeesTo: z.coerce.number().nullish(),
	linkedinUrl: z.string().nullish(),
	websiteUrl: z.string().nullish(),
});

export const CompanyRowScoreSchema = z.object({
	result: z.string().nullish(),
	category: z.string().nullish(),
});

export const CompanyRowInfoSchema = z.object({
	id: z.number(),
	prospectId: z.number(),
	organizationId: z.number(),
	companyFindPersonStatus: z.nativeEnum(CompanyFindPersonStatus).nullish(),
	bucket: z.nativeEnum(CompanyShowBucket),
	blacklisted: z.boolean(),
	addedAt: z.coerce.date(),
	/** Why the company was excluded before any search, in the filter run's own words (PREC-4940). */
	exclusionReason: z.string().nullish(),
	searchedAt: z.coerce.date().nullish(),
	organization: CompanyRowOrganizationSchema,
	score: CompanyRowScoreSchema.nullish(),
	peopleFound: z.array(CompanyPersonFoundSchema),
	peopleFoundCount: z.coerce.number(),
});

export const CompanyRowsPageSchema = z.object({
	rows: z.array(CompanyRowInfoSchema),
	count: z.coerce.number(),
	buckets: z.record(z.nativeEnum(CompanyShowBucket), z.coerce.number()),
});

export type CompanyPersonFound = z.infer<typeof CompanyPersonFoundSchema>;
export type CompanyRowInfo = z.infer<typeof CompanyRowInfoSchema>;
export type CompanyRowsPage = z.infer<typeof CompanyRowsPageSchema>;

export interface GetListCompaniesParams {
	listId: number;
	companyShow?: CompanyShowBucket;
	searchValue?: string;
	page?: number;
	limit?: number;
	sortBy?: string;
	sortOrder?: "ASC" | "DESC";
}

export const listCompaniesApi = api.injectEndpoints({
	endpoints: (builder) => ({
		getListCompanies: builder.query<CompanyRowsPage, GetListCompaniesParams>({
			query: (params) => ({
				url: "list_person/companies",
				params,
				responseSchema: CompanyRowsPageSchema,
			}),
			providesTags: ["ListPerson"],
		}),
	}),
});

export const { useGetListCompaniesQuery, useLazyGetListCompaniesQuery } = listCompaniesApi;
